import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Search,
  Plus,
  Filter,
  Calendar,
  FolderKanban,
  AlertTriangle,
  ArrowRight,
  Trash2,
  LayoutGrid,
  GanttChartSquare,
  Columns3,
  Tag as TagIcon,
  Check,
  Archive,
  Hourglass,
  X,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { listarProjetos, deletarProjeto, atualizarProjeto, Projeto } from "../../services/projetos";
import { listarTiposProjeto } from "../../services/tiposProjeto";
import { listarProjetoTags, ProjetoTag } from "../../services/projetoTags";
import { TagChip } from "../../components/TagChip";
import { ProjetoAvatar } from "../../components/ProjetoAvatar";
import { confirmarDuasVezes } from "../../utils/confirmarDuasVezes";
import { useLembrarScroll } from "../../hooks/useLembrarScroll";

// Helper: slugs (CSV) das tags do projeto.
function slugsDoProjeto(p: Projeto): string[] {
  return (p as any).etiquetas ? String((p as any).etiquetas).split(/[,\s]+/).filter(Boolean) : [];
}

// =============================================================================
// Troca RÁPIDA de tags direto no card (jul/2026) — evita ter que abrir o projeto
// e editar. Só gestor/admin. Vive dentro de um <Link>, então TODO clique
// precisa de stopPropagation+preventDefault pra não navegar.
// =============================================================================
type TagCat = ProjetoTag;

function TagsRapidas({ projeto, tags, podeEditar, salvando, onAlternar }: {
  projeto: Projeto;
  tags: TagCat[];
  podeEditar: boolean;
  salvando: boolean;
  onAlternar: (projetoId: string, slugs: string[]) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const atuais = slugsDoProjeto(projeto);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!aberto) return;
    function fora(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setAberto(false);
    }
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, [aberto]);

  function alternar(e: React.MouseEvent, slug: string) {
    e.preventDefault();
    e.stopPropagation();
    const novos = atuais.includes(slug) ? atuais.filter((s) => s !== slug) : [...atuais, slug];
    onAlternar(projeto.id, novos);
  }

  return (
    <span className="relative inline-flex items-center gap-1.5 flex-wrap" ref={boxRef}>
      {atuais.map((slug) => (
        <TagChip key={slug} tag={tags.find((t) => t.slug === slug)} slug={slug} tamanho="xs" />
      ))}
      {podeEditar && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setAberto((v) => !v); }}
          disabled={salvando}
          className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full border border-dashed border-slate-300 dark:border-slate-600 text-slate-400 hover:text-blue-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50"
          title="Trocar tags deste projeto"
        >
          <TagIcon size={10} />
          {atuais.length === 0 && <span>tags</span>}
        </button>
      )}
      {aberto && (
        <div
          className="absolute z-30 top-full left-0 mt-1 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-2"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        >
          <div className="flex items-center gap-1 mb-1.5 px-0.5">
            <p className="text-[10px] uppercase tracking-wide text-slate-400">Tags do projeto</p>
            {/* Botão "i": explica onde criar tags novas (tooltip no hover/foco). */}
            <span className="relative group/info inline-flex">
              <span
                tabIndex={0}
                role="img"
                aria-label="Onde criar tags"
                className="w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-500 text-slate-400 text-[9px] font-bold flex items-center justify-center cursor-help hover:border-blue-400 hover:text-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                i
              </span>
              <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-52 rounded-md bg-slate-800 dark:bg-slate-900 text-white text-[11px] leading-snug px-2 py-1.5 shadow-lg opacity-0 group-hover/info:opacity-100 group-focus-within/info:opacity-100 transition-opacity z-40">
                As tags podem ser criadas na aba <strong>Tags de projeto</strong> (menu Admin).
              </span>
            </span>
          </div>
          {tags.length === 0 ? (
            <p className="text-[11px] text-slate-400 px-0.5 py-1">
              Nenhuma tag. Crie em Admin → Tags de projeto.
            </p>
          ) : (
            <div className="flex flex-col gap-0.5 max-h-56 overflow-y-auto">
              {tags.map((t) => {
                const marcada = atuais.includes(t.slug);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={(e) => alternar(e, t.slug)}
                    disabled={salvando}
                    className={`flex items-center gap-2 px-1.5 py-1 rounded text-left text-xs transition-colors disabled:opacity-50 ${
                      marcada ? "bg-blue-50 dark:bg-blue-900/30" : "hover:bg-slate-50 dark:hover:bg-slate-700/60"
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        marcada ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300 dark:border-slate-500"
                      }`}
                    >
                      {marcada && <Check size={10} />}
                    </span>
                    <span className="shrink-0">{t.icone}</span>
                    <span className="truncate text-slate-700 dark:text-slate-200">{t.nome}</span>
                    <span className="ml-auto w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.cor }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </span>
  );
}

const statusLabels: Record<string, string> = {
  planejamento: "Planejamento",
  ativo: "Ativo",
  em_validacao: "Em validação",
  pausado: "Pausado",
  entregue: "Entregue (suporte)",
  concluido: "Concluído",
};

const statusColors: Record<string, string> = {
  planejamento: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  ativo: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  em_validacao: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  pausado: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  entregue: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  concluido: "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
};

// PRIORIDADE DO PROJETO REMOVIDA DA UI (pedido da Rec, jul/2026: "os projetos
// têm todos a mesma importância") — substituída por TAGS personalizadas
// (catálogo ProjetoTag). O campo prioridade segue no banco por compat, mas não
// aparece, não ordena e não colore mais nada.

// Status que significam "trabalho terminado" — pra esses, não faz sentido falar de prazo/atraso.
const STATUS_FINALIZADOS = new Set(["entregue", "concluido"]);

function diasParaPrazo(
  prazoStr: string | null | undefined,
  status?: string,
  aguardandoCliente?: boolean,
): { texto: string; cor: string; dias: number | null } {
  if (!prazoStr) return { texto: "sem prazo", cor: "text-slate-400 dark:text-slate-500", dias: null };
  const prazo = new Date(prazoStr);
  prazo.setHours(0, 0, 0, 0);
  // Prazo inválido/epoch (1969/1970) = trata como sem prazo (não "atrasado 20630d").
  if (isNaN(prazo.getTime()) || prazo.getFullYear() < 2000) {
    return { texto: "sem prazo", cor: "text-slate-400 dark:text-slate-500", dias: null };
  }
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const dias = Math.round((prazo.getTime() - hoje.getTime()) / 86400000);
  // Projeto finalizado: não mostra "atrasado". Mostra a data de entrega prevista.
  if (status && STATUS_FINALIZADOS.has(status)) {
    return { texto: prazo.toLocaleDateString("pt-BR"), cor: "text-slate-500 dark:text-slate-400", dias };
  }
  // Aguardando cliente: atraso não é da equipe — pill roxo neutro. dias:null tira o ícone de alerta.
  if (aguardandoCliente) {
    return { texto: "aguardando cliente", cor: "text-purple-600 dark:text-purple-400 font-medium", dias: null };
  }
  if (dias < 0) return { texto: `atrasado ${Math.abs(dias)}d`, cor: "text-red-700 dark:text-red-400 font-semibold", dias };
  if (dias === 0) return { texto: "hoje", cor: "text-red-600 dark:text-red-400 font-semibold", dias };
  if (dias === 1) return { texto: "amanhã", cor: "text-orange-600 dark:text-orange-400 font-semibold", dias };
  if (dias <= 7) return { texto: `em ${dias} dias`, cor: "text-orange-600 dark:text-orange-400", dias };
  if (dias <= 30) return { texto: `em ${dias} dias`, cor: "text-amber-600 dark:text-amber-400", dias };
  return { texto: `em ${dias} dias`, cor: "text-slate-500 dark:text-slate-400", dias };
}

type Visao = "cards" | "timeline" | "kanban";

// Persistência das preferências de exibição em localStorage.
// As escolhas viajam entre reloads e abas — busca/status/filtros voláteis ficam de fora.
const KEY_VISAO = "projetos_visao";
const KEY_INTERNO = "projetos_interno_mode";
const KEY_ARQUIVADOS = "projetos_mostrar_arquivados";

function lerStorage<T extends string>(key: string, valido: readonly T[], fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(key) as T | null;
  return raw && valido.includes(raw) ? raw : fallback;
}
function lerBoolStorage(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  const raw = window.localStorage.getItem(key);
  if (raw === "1") return true;
  if (raw === "0") return false;
  return fallback;
}

export function ProjetosList() {
  const { can } = useAuth();
  const ehSuporte = can(["suporte"]);
  const [busca, setBusca] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  // Modalidade do contrato (substituiu o filtro de "categoria", que nunca funcionou).
  const [filtroModalidade, setFiltroModalidade] = useState<"" | "mensal" | "avulso">("");
  // Situação (deep-link dos cards da dashboard, jul/2026): "ativos" |
  // "atrasados" | "aguardando". Espelha as contagens do dashboardRoutes. Fica
  // fora do storage — é um recorte que só vale enquanto a pessoa quiser vê-lo.
  const [filtroSituacao, setFiltroSituacao] = useState<"" | "ativos" | "atrasados" | "aguardando">("");
  const [searchParams, setSearchParams] = useSearchParams();
  // Toggle Externos | Internos. Default: externos. Persistido.
  // Suporte fica travado em "internos" (não tem acesso a externos).
  const [internoMode, setInternoMode] = useState<"externos" | "internos">(() =>
    ehSuporte ? "internos" : lerStorage(KEY_INTERNO, ["externos", "internos"] as const, "externos"),
  );
  // Mostra também os arquivados na listagem. Persistido.
  const [mostrarArquivados, setMostrarArquivados] = useState<boolean>(() =>
    lerBoolStorage(KEY_ARQUIVADOS, false),
  );
  // Visão. Persistida. O Kanban voltou (pedido do Ricardo, jul/2026) mas agora
  // agrupa por TAG de projeto — as colunas são as tags do catálogo.
  const [visao, setVisao] = useState<Visao>(() =>
    lerStorage(KEY_VISAO, ["cards", "timeline", "kanban"] as const, "cards"),
  );
  // Filtro por TAG de projeto (substitui a antiga ordenação por prioridade).
  const [filtroTag, setFiltroTag] = useState("");

  // Deep-link dos cards de GESTÃO da dashboard: ?situacao=ativos|atrasados|
  // aguardando. Roda UMA vez no mount (guard useRef): ativa o recorte, força
  // "externos" (esses cards são de projetos de cliente, e o backend conta só
  // externos) e limpa o param da URL — F5 não trava e o X do badge zera.
  const deepLinkAplicado = useRef(false);
  useEffect(() => {
    if (deepLinkAplicado.current) return;
    deepLinkAplicado.current = true;
    const s = searchParams.get("situacao");
    if (s !== "ativos" && s !== "atrasados" && s !== "aguardando") return;
    setFiltroSituacao(s);
    if (!ehSuporte) setInternoMode("externos");
    const limpo = new URLSearchParams(searchParams);
    limpo.delete("situacao");
    setSearchParams(limpo, { replace: true });
  }, []);

  // Salva mudanças no storage
  useEffect(() => { try { localStorage.setItem(KEY_VISAO, visao); } catch {} }, [visao]);
  useEffect(() => { try { localStorage.setItem(KEY_INTERNO, internoMode); } catch {} }, [internoMode]);
  useEffect(() => { try { localStorage.setItem(KEY_ARQUIVADOS, mostrarArquivados ? "1" : "0"); } catch {} }, [mostrarArquivados]);

  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["projetos", { status: filtroStatus, interno: internoMode, arquivados: mostrarArquivados, situacao: filtroSituacao }],
    queryFn: () => listarProjetos({
      status: filtroStatus || undefined,
      interno: internoMode === "internos",
      arquivados: mostrarArquivados ? "all" : false,
      // Recorte da dashboard: filtra no BACKEND (mesmos wheres do card, senão
      // não bate).
      situacao: filtroSituacao || undefined,
      // TRAZ TODOS, SEMPRE. Esta tela NÃO tem paginação e a busca é feita no
      // navegador, sobre o que já foi carregado — com o default de 20 do backend
      // ela enxergava só os 20 projetos mais RECENTES e os outros 37 eram
      // invisíveis E impossíveis de achar na busca (a Thainá não achava o
      // "Mutato | Projeto mensal", que estava em 26º). O `perPage: 500` já
      // existia aqui, mas só quando vinha o recorte da dashboard.
      perPage: 500,
    }),
  });

  const { data: tiposProjeto = [] } = useQuery({
    queryKey: ["tipos-projeto", "ativos"],
    queryFn: () => listarTiposProjeto(),
  });

  // Catálogo de tags de projeto (só ativas) — filtro + chips dos cards.
  const { data: projetoTags = [] } = useQuery({
    queryKey: ["projeto-tags"],
    queryFn: () => listarProjetoTags(),
  });

  const deletarM = useMutation({
    mutationFn: deletarProjeto,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projetos"] }),
  });

  // Troca rápida de tags direto no card (sem abrir o projeto). Gestor/admin.
  const tagsRapidasM = useMutation({
    mutationFn: ({ id, slugs }: { id: string; slugs: string[] }) => atualizarProjeto(id, { etiquetas: slugs }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["projeto", v.id] });
    },
  });
  const podeEditarTags = can(["gestor", "admin"]);

  // Volta pra onde ela estava na lista ao sair de um projeto (pedido da Thainá,
  // ago/2026). Só restaura depois que a lista carregou — ver o hook.
  const ancoraScroll = useLembrarScroll("projetos_scroll", !isLoading);

  // Troca rápida de STATUS direto no card (pedido da Thainá, ago/2026: "ou até
  // aqui mesmo ter a opção de concluir"). Mesmo espírito da troca de tags: sem
  // abrir o projeto e sem passar pela tela de edição — ela está revisando
  // dezenas de projetos de uma vez.
  const statusRapidoM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => atualizarProjeto(id, { status }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["projeto", v.id] });
      // A dashboard conta atrasados/concluídos — precisa refrescar junto.
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  function handleExcluir(e: React.MouseEvent, projeto: Projeto) {
    e.preventDefault();
    e.stopPropagation();
    const ok = confirmarDuasVezes({
      primeiraMensagem: `Excluir definitivamente o projeto "${projeto.nome}"?\n\nIsso apagará tarefas, anexos, comentários, contratos e mensagens vinculados.`,
      segundaMensagem: `CONFIRMAR exclusão de "${projeto.nome}"?\n\nEsta é a confirmação FINAL — todos os dados serão perdidos para sempre.`,
    });
    if (ok) deletarM.mutate(projeto.id);
  }

  const projetos = (data?.projetos as Projeto[]) || [];

  const isAtrasado = (projeto: Projeto) => {
    if (!projeto.prazoEntrega) return false;
    if (STATUS_FINALIZADOS.has(projeto.status)) return false;
    // Aguardando cliente = atraso não é da equipe; não marca vermelho (bate com a dashboard).
    if ((projeto as any).aguardandoCliente) return false;
    return new Date(projeto.prazoEntrega) < new Date();
  };

  const totalArquivados = projetos.filter((p) => p.arquivado).length;

  // Filtro + ordenação memoizados. Antes era recomputado a cada render — incluindo
  // ao trocar o toggle de visão Cards/Kanban/Timeline. O `||` em `p.cliente` é defensivo:
  // projetos legados criados antes do fallback "Interno Growsoft" podem ter cliente=null,
  // o que crashava `p.cliente.toLowerCase()` durante o render e travava a aba.
  const projetosFiltrados = useMemo(() => {
    const buscaLc = busca.toLowerCase();
    return projetos
      .filter((p) => {
        const nomeLc = (p.nome || "").toLowerCase();
        const clienteLc = (p.cliente || "").toLowerCase();
        return (
          nomeLc.includes(buscaLc) ||
          (!p.interno && clienteLc.includes(buscaLc))
        );
      })
      .filter((p) => !filtroModalidade || (p as any).modalidade === filtroModalidade)
      // Filtro por TAG de projeto (substituiu a prioridade).
      .filter((p) => !filtroTag || slugsDoProjeto(p).includes(filtroTag))
      // O recorte de situação é filtrado no BACKEND (a lista não traz
      // `aguardandoCliente` por projeto, e pagina em 20) — aqui não refiltra.
      .sort((a, b) => {
        // Entregues vão pro final
        const aEntr = a.status === "entregue" ? 1 : 0;
        const bEntr = b.status === "entregue" ? 1 : 0;
        if (aEntr !== bEntr) return aEntr - bEntr;
        // Data (única ordenação — prioridade saiu): atrasados primeiro,
        // depois prazo crescente, sem-prazo no fim.
        const aAtr = isAtrasado(a) ? 0 : 1;
        const bAtr = isAtrasado(b) ? 0 : 1;
        if (aAtr !== bAtr) return aAtr - bAtr;
        const aT = a.prazoEntrega ? new Date(a.prazoEntrega).getTime() : Infinity;
        const bT = b.prazoEntrega ? new Date(b.prazoEntrega).getTime() : Infinity;
        return aT - bT;
      });
  }, [projetos, busca, filtroModalidade, filtroTag]);

  return (
    <div className="space-y-6" ref={ancoraScroll}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
            {internoMode === "internos" ? "Projetos Internos" : "Projetos"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {projetos.length} projeto{projetos.length !== 1 && "s"} no total
          </p>
        </div>
        {can(["funcionario", "suporte", "gestor", "admin"]) && (
          <Link
            to={`/projetos/novo${internoMode === "internos" ? "?interno=1" : ""}`}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
          >
            <Plus size={18} />
            {internoMode === "internos" ? "Novo Projeto Interno" : "Novo Projeto"}
          </Link>
        )}
      </div>

      {/* Toggle Externos | Internos + Arquivados. Suporte não vê o toggle: já fica travado em internos. */}
      {!ehSuporte && can(["gestor", "admin", "funcionario"]) && (
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
            <button
              type="button"
              onClick={() => setInternoMode("externos")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                internoMode === "externos"
                  ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              Externos
            </button>
            <button
              type="button"
              onClick={() => setInternoMode("internos")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                internoMode === "internos"
                  ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              Internos
            </button>
          </div>

          <button
            type="button"
            onClick={() => setMostrarArquivados((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              mostrarArquivados
                ? "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700"
                : "bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
            }`}
            title={mostrarArquivados ? "Ocultar arquivados" : "Exibir arquivados também"}
          >
            <Archive size={14} />
            {mostrarArquivados ? "Mostrando arquivados" : "Exibir arquivados"}
            {mostrarArquivados && totalArquivados > 0 && (
              <span className="bg-amber-200 dark:bg-amber-700/60 text-amber-900 dark:text-amber-100 text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums">
                {totalArquivados}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Badge do recorte de situação (deep-link da dashboard). Fica visível pra
          a pessoa saber que a lista está filtrada e poder limpar num clique. */}
      {filtroSituacao && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-700">
            {filtroSituacao === "ativos" && <><FolderKanban size={12} /> Só projetos ativos</>}
            {filtroSituacao === "atrasados" && <><AlertTriangle size={12} /> Só projetos atrasados</>}
            {filtroSituacao === "aguardando" && <><Hourglass size={12} /> Só aguardando cliente</>}
            {/* Contagem do SERVIDOR (bate com o card da dashboard). */}
            <span className="text-blue-500 dark:text-blue-400">· {data?.total ?? projetosFiltrados.length}</span>
            <button
              type="button"
              onClick={() => setFiltroSituacao("")}
              className="ml-0.5 hover:text-red-600 dark:hover:text-red-400"
              title="Limpar filtro"
              aria-label="Limpar filtro"
            >
              <X size={13} />
            </button>
          </span>
        </div>
      )}

      {/* Filtros + Controles de visão/ordenação */}
      <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome ou cliente..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40 outline-none"
          />
        </div>

        <div className="relative">
          <Filter
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="pl-10 pr-8 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40 outline-none appearance-none bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
          >
            <option value="">Todos os status</option>
            <option value="planejamento">Planejamento</option>
            <option value="ativo">Ativo</option>
            <option value="em_validacao">Em validação</option>
            <option value="pausado">Pausado</option>
            <option value="entregue">Entregue (suporte)</option>
            <option value="concluido">Concluído</option>
          </select>
        </div>

        {/* MODALIDADE no lugar da antiga "categoria" (decisão da Thainá,
            ago/2026: "pode tirar essa categoria, dividir apenas em mensal e
            avulso"). A categoria era um campo que o cadastro nunca preencheu —
            filtrava zero sempre — e as opções dela ("Mensal"/"Avulso") já eram a
            modalidade, que é preenchida de verdade e alimenta os relatórios. */}
        <div className="relative">
          <select
            value={filtroModalidade}
            onChange={(e) => setFiltroModalidade(e.target.value as "" | "mensal" | "avulso")}
            title="Filtrar por modalidade de contrato"
            className="pl-3 pr-8 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40 outline-none appearance-none bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
          >
            <option value="">Mensais e avulsos</option>
            <option value="mensal">Só mensais</option>
            <option value="avulso">Só avulsos</option>
          </select>
        </div>

        {/* Filtro por TAG de projeto (substituiu "Organizar: Prioridade" — a
            lista agora ordena sempre por data/atraso). */}
        {projetoTags.length > 0 && (
          <div className="relative">
            <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={filtroTag}
              onChange={(e) => setFiltroTag(e.target.value)}
              className="pl-10 pr-8 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-900/40 outline-none appearance-none bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              title="Filtrar por tag"
            >
              <option value="">Todas as tags</option>
              {projetoTags.map((t) => (
                <option key={t.id} value={t.slug}>{t.icone} {t.nome}</option>
              ))}
            </select>
          </div>
        )}

        {/* Toggle visão Cards | Timeline | Kanban */}
        <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
          <button
            type="button"
            onClick={() => setVisao("cards")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              visao === "cards" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
            title="Visualizar em cards"
          >
            <LayoutGrid size={16} /> Cards
          </button>
          <button
            type="button"
            onClick={() => setVisao("kanban")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              visao === "kanban" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
            title="Visualizar como Kanban (colunas por tag)"
          >
            <Columns3 size={16} /> Kanban
          </button>
          <button
            type="button"
            onClick={() => setVisao("timeline")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              visao === "timeline" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
            title="Visualizar em linha do tempo"
          >
            <GanttChartSquare size={16} /> Timeline
          </button>
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="text-center py-12 text-slate-400 dark:text-slate-500">Carregando...</div>
      ) : visao === "timeline" ? (
        <ProjetosTimeline projetos={projetosFiltrados} />
      ) : visao === "kanban" ? (
        <ProjetosKanbanTags
          projetos={projetosFiltrados}
          tags={projetoTags}
          tiposProjeto={tiposProjeto}
          podeEditarTags={podeEditarTags}
          salvandoTags={tagsRapidasM.isPending}
          onAlternarTags={(id, slugs) => tagsRapidasM.mutate({ id, slugs })}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projetosFiltrados.map((projeto) => {
            const atrasado = isAtrasado(projeto);
            const entregue = projeto.status === "entregue" || projeto.status === "concluido";
            const arquivado = !!projeto.arquivado;
            // Sem cor de prioridade (saiu): card neutro; só atraso/arquivado colorem.
            const corClasses = entregue
              ? "bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
              : "bg-white dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-700";
            // Arquivado override: visual cinza + tracejado + saturação reduzida.
            // Atrasado override (só pra ativos não arquivados): vermelho intenso.
            const finalClasses = arquivado
              ? "bg-slate-50 dark:bg-slate-900/40 border-slate-300 dark:border-slate-600 border-dashed opacity-70 hover:opacity-100 grayscale hover:grayscale-0"
              : atrasado && !entregue
                ? "bg-red-100 dark:bg-red-950/60 border-red-500 dark:border-red-600 hover:border-red-600 ring-1 ring-red-300 dark:ring-red-800"
                : corClasses;
            return (
            <Link
              key={projeto.id}
              to={`/projetos/${projeto.id}`}
              className={`block rounded-xl p-5 hover:shadow-lg transition-all group border-l-4 border-y border-r ${finalClasses}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <ProjetoAvatar
                    nome={projeto.nome}
                    fotoUrl={(projeto as any).fotoUrl}
                    cor={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.cor || "#64748b"}
                    tamanho="md"
                    titulo={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.label || projeto.tipo}
                  />
                  <div>
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2 flex-wrap">
                      {projeto.nome}
                      {projeto.interno && (
                        <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-medium uppercase tracking-wide whitespace-nowrap">
                          Interno
                        </span>
                      )}
                      {/* MELHORIA 26 — rótulo de periodicidade */}
                      {(projeto as any).periodicidade && (
                        <span className="px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-[10px] font-medium uppercase tracking-wide whitespace-nowrap">
                          {(projeto as any).periodicidade}
                        </span>
                      )}
                      {projeto.arquivado && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[10px] font-medium uppercase tracking-wide whitespace-nowrap">
                          <Archive size={10} /> Arquivado
                        </span>
                      )}
                      {(projeto as any).aguardandoCliente && !projeto.interno && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] font-medium uppercase tracking-wide whitespace-nowrap">
                          <Hourglass size={10} /> Aguardando cliente
                        </span>
                      )}
                    </h3>
                    {!projeto.interno && (
                      <p className="text-sm text-slate-500 dark:text-slate-400">{projeto.cliente}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {can(["admin"]) && (
                    <button
                      type="button"
                      onClick={(e) => handleExcluir(e, projeto)}
                      disabled={deletarM.isPending}
                      className="p-1.5 rounded-md text-slate-300 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                      title="Excluir projeto definitivamente"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                  <ArrowRight
                    size={18}
                    className="text-slate-300 dark:text-slate-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors"
                  />
                </div>
              </div>

              <div className="flex items-center gap-4 mt-4 flex-wrap">
                {/* O selo de status é editável aqui mesmo. O card inteiro é um
                    Link, então o select precisa segurar o clique (preventDefault
                    + stopPropagation) — senão escolher um status abriria o
                    projeto. Mesmo cuidado que o TagsRapidas ao lado já toma. */}
                {podeEditarTags ? (
                  <span
                    className="relative inline-flex"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  >
                    <select
                      value={projeto.status}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        statusRapidoM.mutate({ id: projeto.id, status: e.target.value });
                      }}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      disabled={statusRapidoM.isPending}
                      title="Trocar o status deste projeto"
                      className={`appearance-none cursor-pointer pl-2.5 pr-6 py-1 rounded text-xs font-medium outline-none border border-transparent hover:border-slate-300 dark:hover:border-slate-500 focus:border-blue-500 disabled:opacity-50 ${statusColors[projeto.status]}`}
                    >
                      {Object.entries(statusLabels).map(([valor, rotulo]) => (
                        <option key={valor} value={valor}>{rotulo}</option>
                      ))}
                    </select>
                    <ChevronDown size={12} className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 opacity-60" />
                  </span>
                ) : (
                  <span className={`px-2.5 py-1 rounded text-xs font-medium ${statusColors[projeto.status]}`}>
                    {statusLabels[projeto.status]}
                  </span>
                )}
                {/* Tags do projeto + troca rápida (substituem o selo de prioridade). */}
                <TagsRapidas
                  projeto={projeto}
                  tags={projetoTags}
                  podeEditar={podeEditarTags}
                  salvando={tagsRapidasM.isPending}
                  onAlternar={(id, slugs) => tagsRapidasM.mutate({ id, slugs })}
                />
              </div>

              <div className="flex items-center gap-6 mt-4 text-sm flex-wrap">
                {(() => {
                  const dp = diasParaPrazo(projeto.prazoEntrega, projeto.status, (projeto as any).aguardandoCliente);
                  const ehFinalizado = projeto.status === "entregue" || projeto.status === "concluido";
                  const Icone = !ehFinalizado && dp.dias !== null && dp.dias < 0 ? AlertTriangle : Calendar;
                  return (
                    <div className={`flex items-center gap-1.5 ${dp.cor}`}>
                      <Icone size={14} />
                      <span title={projeto.prazoEntrega ? new Date(projeto.prazoEntrega).toLocaleDateString("pt-BR") : ""}>
                        {dp.texto}
                      </span>
                    </div>
                  );
                })()}
                <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                  <FolderKanban size={14} />
                  <span>{projeto._count?.tarefas || 0} tarefas</span>
                </div>
                <div className="text-slate-500 dark:text-slate-400">Atend.: {projeto.responsavel?.nome}</div>
              </div>
            </Link>
            );
          })}
        </div>
      )}

      {/* VAZIO POR FILTRO ≠ vazio de verdade. A Thainá filtrou por categoria,
          viu "60 projetos no total" com a lista vazia e achou que era bug. Se
          há filtro ligado, a tela diz QUAIS são e oferece limpar. */}
      {!isLoading && projetosFiltrados.length === 0 && (() => {
        const ativos: Array<{ rotulo: string; limpar: () => void }> = [];
        if (busca.trim()) ativos.push({ rotulo: `busca "${busca.trim()}"`, limpar: () => setBusca("") });
        if (filtroStatus) ativos.push({ rotulo: `status "${statusLabels[filtroStatus] || filtroStatus}"`, limpar: () => setFiltroStatus("") });
        if (filtroModalidade) {
          ativos.push({
            rotulo: filtroModalidade === "mensal" ? "só contratos mensais" : "só projetos avulsos",
            limpar: () => setFiltroModalidade(""),
          });
        }
        if (filtroTag) ativos.push({ rotulo: `tag "${projetoTags.find((t) => t.slug === filtroTag)?.nome || filtroTag}"`, limpar: () => setFiltroTag("") });
        if (filtroSituacao) ativos.push({ rotulo: "recorte da dashboard", limpar: () => setFiltroSituacao("") });

        if (ativos.length === 0) {
          return (
            <div className="text-center py-12 text-slate-400 dark:text-slate-500">
              Nenhum projeto encontrado
            </div>
          );
        }
        return (
          <div className="text-center py-12">
            <p className="text-slate-500 dark:text-slate-400 font-medium">Nenhum projeto com esses filtros</p>
            <p className="text-sm text-slate-400 mt-1">
              Filtrando por {ativos.map((f) => f.rotulo).join(" + ")}.
            </p>
            <button
              type="button"
              onClick={() => ativos.forEach((f) => f.limpar())}
              className="mt-3 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
            >
              Limpar {ativos.length === 1 ? "o filtro" : "os filtros"}
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// =============================================================================
// Timeline (Gantt-like) — barras horizontais por projeto entre dataInicio e
// prazoEntrega, com linha vertical do "hoje" e cor por prioridade.
// =============================================================================
function ProjetosTimeline({ projetos }: { projetos: Projeto[] }) {
  // Exige datas presentes E parsáveis — uma só data NaN faz Math.min/max retornar NaN
  // e quebra o cálculo de marcadores/larguras (já vimos travar pra dados ruins).
  const comDatas = projetos.filter((p) => {
    if (!p.dataInicio || !p.prazoEntrega) return false;
    const ini = new Date(p.dataInicio).getTime();
    const fim = new Date(p.prazoEntrega).getTime();
    return Number.isFinite(ini) && Number.isFinite(fim);
  });
  if (comDatas.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-400 dark:text-slate-500 text-sm">
        Nenhum projeto com datas definidas para exibir na timeline.
      </div>
    );
  }

  // Calcula extremos com padding lateral de ~7 dias pra não colar nas bordas.
  const inicios = comDatas.map((p) => new Date(p.dataInicio).getTime());
  const fins = comDatas.map((p) => new Date(p.prazoEntrega!).getTime());
  const hoje = Date.now();
  const min = Math.min(...inicios, hoje) - 7 * 86400000;
  const max = Math.max(...fins, hoje) + 7 * 86400000;
  const totalMs = max - min;

  function pct(t: number) {
    return ((t - min) / totalMs) * 100;
  }

  // Marcadores de mês ao longo do eixo
  const marcadores: Array<{ pos: number; label: string }> = [];
  const cursor = new Date(min);
  cursor.setDate(1);
  cursor.setHours(0, 0, 0, 0);
  while (cursor.getTime() < max) {
    if (cursor.getTime() >= min) {
      const label = cursor.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
      marcadores.push({ pos: pct(cursor.getTime()), label });
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const hojePct = pct(hoje);
  const naoEntregues = comDatas.filter((p) => p.status !== "entregue" && p.status !== "concluido");

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Cabeçalho com meses */}
      <div className="relative h-8 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
        {marcadores.map((m, i) => (
          <div
            key={i}
            className="absolute top-0 bottom-0 flex items-center text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 border-l border-slate-200 dark:border-slate-700 pl-1.5"
            style={{ left: `${m.pos}%` }}
          >
            {m.label}
          </div>
        ))}
      </div>

      {/* Linhas */}
      <div className="relative">
        {/* Linha do "hoje" */}
        {hojePct >= 0 && hojePct <= 100 && (
          <div
            className="absolute top-0 bottom-0 w-px bg-red-500/70 z-20 pointer-events-none"
            style={{ left: `${hojePct}%` }}
          >
            <span className="absolute -top-0 -translate-y-full left-1/2 -translate-x-1/2 text-[10px] font-semibold text-red-600 dark:text-red-400 bg-white dark:bg-slate-800 px-1 rounded whitespace-nowrap">
              hoje
            </span>
          </div>
        )}

        {comDatas.map((projeto) => {
          const ini = new Date(projeto.dataInicio).getTime();
          const fim = new Date(projeto.prazoEntrega!).getTime();
          const left = pct(ini);
          const width = Math.max(pct(fim) - left, 0.8);
          const dp = diasParaPrazo(projeto.prazoEntrega, projeto.status, (projeto as any).aguardandoCliente);
          const atrasado = naoEntregues.includes(projeto) && fim < hoje && !(projeto as any).aguardandoCliente;
          // Sem cor de prioridade: azul padrão; vermelho só quando atrasado.
          const corBarra = atrasado ? "bg-red-500" : "bg-blue-500";

          return (
            <Link
              key={projeto.id}
              to={`/projetos/${projeto.id}`}
              className="block relative h-12 border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors group"
            >
              <div
                className={`absolute top-1/2 -translate-y-1/2 h-6 rounded ${corBarra} ${
                  projeto.status === "entregue" || projeto.status === "concluido" ? "opacity-50" : ""
                } shadow-sm group-hover:shadow-md transition-shadow flex items-center px-2 text-white text-xs font-medium overflow-hidden whitespace-nowrap`}
                style={{ left: `${left}%`, width: `${width}%`, minWidth: "4px" }}
                title={`${projeto.nome} — ${dp.texto}`}
              >
                <span className="truncate">{projeto.nome}</span>
              </div>

              {/* Label fora da barra (à direita) com dias-restantes, se couber */}
              {width < 30 && (
                <div
                  className={`absolute top-1/2 -translate-y-1/2 text-xs ${dp.cor} pl-2 pointer-events-none whitespace-nowrap`}
                  style={{ left: `${Math.min(left + width, 95)}%` }}
                >
                  {projeto.nome} <span className="text-[11px]">· {dp.texto}</span>
                </div>
              )}
            </Link>
          );
        })}
      </div>

      {/* Legenda (prioridade saiu — só atraso colore) */}
      <div className="flex items-center gap-4 px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500" /> No prazo</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500" /> Atrasado</span>
        <span className="flex items-center gap-1.5 ml-auto"><span className="w-px h-3 bg-red-500" /> Hoje</span>
      </div>
    </div>
  );
}


// =============================================================================
// Kanban por TAG (jul/2026) — cada tag do catálogo vira uma coluna; o projeto
// aparece na coluna de cada tag que tem. Substituiu o kanban por prioridade.
// Visualização apenas (sem drag). Projetos sem nenhuma tag caem em "Sem tag".
// =============================================================================
function ProjetosKanbanTags({ projetos, tags, tiposProjeto, podeEditarTags, salvandoTags, onAlternarTags }: {
  projetos: Projeto[];
  tags: TagCat[];
  tiposProjeto: Array<{ slug: string; cor: string | null; icone: string; label: string }>;
  podeEditarTags: boolean;
  salvandoTags: boolean;
  onAlternarTags: (id: string, slugs: string[]) => void;
}) {
  if (tags.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-12 text-center text-slate-400 dark:text-slate-500 text-sm">
        Nenhuma tag de projeto cadastrada. Crie em <strong>Admin → Tags de projeto</strong> para usar esta visão.
      </div>
    );
  }

  // Um projeto com N tags aparece nas N colunas (de propósito — é o "onde ele se
  // encaixa"). Sem tag nenhuma, vai pra coluna final "Sem tag".
  const colunas = tags.map((t) => ({
    chave: t.slug,
    titulo: t.nome,
    icone: t.icone,
    cor: t.cor,
    lista: projetos.filter((p) => slugsDoProjeto(p).includes(t.slug)),
  }));
  const semTag = projetos.filter((p) => slugsDoProjeto(p).length === 0);
  if (semTag.length > 0) {
    colunas.push({ chave: "__sem_tag__", titulo: "Sem tag", icone: "—", cor: "#94a3b8", lista: semTag });
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {colunas.map((col) => (
        <div
          key={col.chave}
          className="rounded-xl border border-slate-200/70 dark:border-slate-700/70 bg-slate-50/60 dark:bg-slate-900/30 flex flex-col min-h-[300px] w-[300px] shrink-0"
        >
          <div
            className="px-3 py-2 rounded-t-xl border-b text-sm font-semibold flex items-center justify-between gap-2"
            style={{ background: `${col.cor}1a`, borderColor: `${col.cor}55`, color: col.cor }}
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="shrink-0">{col.icone}</span>
              <span className="uppercase tracking-wide text-xs truncate">{col.titulo}</span>
            </span>
            <span className="text-[11px] font-bold bg-white/80 dark:bg-slate-900/50 rounded-full px-2 py-0.5 tabular-nums shrink-0">
              {col.lista.length}
            </span>
          </div>
          <div className="p-2 space-y-2 flex-1">
            {col.lista.length === 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-6">Nenhum projeto</p>
            )}
            {col.lista.map((projeto) => (
              <KanbanTagCard
                key={`${col.chave}-${projeto.id}`}
                projeto={projeto}
                tiposProjeto={tiposProjeto}
                tags={tags}
                podeEditarTags={podeEditarTags}
                salvandoTags={salvandoTags}
                onAlternarTags={onAlternarTags}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function KanbanTagCard({ projeto, tiposProjeto, tags, podeEditarTags, salvandoTags, onAlternarTags }: {
  projeto: Projeto;
  tiposProjeto: Array<{ slug: string; cor: string | null; icone: string; label: string }>;
  tags: TagCat[];
  podeEditarTags: boolean;
  salvandoTags: boolean;
  onAlternarTags: (id: string, slugs: string[]) => void;
}) {
  const dp = diasParaPrazo(projeto.prazoEntrega, projeto.status, (projeto as any).aguardandoCliente);
  const entregue = projeto.status === "entregue" || projeto.status === "concluido";
  const dpIcon = !entregue && dp.dias !== null && dp.dias < 0 ? <AlertTriangle size={11} /> : <Calendar size={11} />;

  return (
    <Link
      to={`/projetos/${projeto.id}`}
      className={`block bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 shadow-sm hover:shadow-md transition-shadow ${entregue ? "opacity-70" : ""}`}
    >
      <div className="flex items-start gap-2">
        <ProjetoAvatar
          nome={projeto.nome}
          fotoUrl={(projeto as any).fotoUrl}
          cor={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.cor || "#64748b"}
          tamanho="sm"
          titulo={tiposProjeto.find((tp) => tp.slug === projeto.tipo)?.label || projeto.tipo}
          formato="rounded-md"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate" title={projeto.nome}>
            {projeto.nome}
          </p>
          {!projeto.interno && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{projeto.cliente}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded ${statusColors[projeto.status]}`}>
          {statusLabels[projeto.status]}
        </span>
        {(projeto as any).periodicidade && (
          <span className="text-[9px] uppercase font-medium px-1.5 py-0.5 rounded bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300">
            {(projeto as any).periodicidade}
          </span>
        )}
      </div>

      {/* Troca rápida de tags — daqui dá pra "mover" o projeto entre as colunas. */}
      <div className="mt-2">
        <TagsRapidas
          projeto={projeto}
          tags={tags}
          podeEditar={podeEditarTags}
          salvando={salvandoTags}
          onAlternar={onAlternarTags}
        />
      </div>

      <div className="flex items-center gap-3 mt-2 text-[11px] flex-wrap">
        <span className={`flex items-center gap-1 ${dp.cor}`} title={projeto.prazoEntrega ? new Date(projeto.prazoEntrega).toLocaleDateString("pt-BR") : ""}>
          {dpIcon}
          <span>{dp.texto}</span>
        </span>
        <span className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
          <FolderKanban size={11} />
          <span>{projeto._count?.tarefas || 0}</span>
        </span>
      </div>
    </Link>
  );
}
